import React, { useEffect, useState } from 'react';
import './ExitReadinessGauge.css';

/**
 * ExitReadinessGauge Component
 *
 * Displays a circular gauge visualization of the exit readiness score.
 * Includes animated progress ring and readiness level indicator.
 */
const ExitReadinessGauge = ({
  score = 0,
  size = 200,
  strokeWidth = 20,
  showLabel = true,
  label = 'Exit Readiness Score',
  readinessLevel = null,
  animated = true,
  className = ''
}) => {
  const [displayScore, setDisplayScore] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (displayScore / 100) * circumference;

  // Animate score on mount
  useEffect(() => {
    if (!animated) {
      setDisplayScore(score);
      return;
    }

    let startTime = null;
    const duration = 1500; // 1.5 seconds

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(score * easeOutCubic);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [score, animated]);

  // Determine color based on score
  const getScoreColor = (score) => {
    if (score >= 72) return '#2d6a4f'; // Best in Class - Dark Green
    if (score >= 67) return '#52b788'; // Green Zone - Green
    if (score >= 57) return '#74c69d'; // Moderate - Light Green
    if (score >= 43) return '#f4a261'; // Developing - Orange
    if (score >= 28) return '#e76f51'; // Early Stage - Red-Orange
    return '#d62828'; // Beginning Journey - Red
  };

  const color = getScoreColor(score);

  return (
    <div className={`exit-readiness-gauge ${className}`}>
      <svg
        width={size}
        height={size}
        className="gauge-svg"
      >
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />

        {/* Progress Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="gauge-progress"
        />

        {/* Center Text */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy=".3em"
          className="gauge-score"
          fill={color}
        >
          {Math.round(displayScore)}%
        </text>
      </svg>

      {/* Label */}
      {showLabel && (
        <div className="gauge-label">
          {label}
        </div>
      )}

      {/* Readiness Level Badge */}
      {readinessLevel && (
        <div
          className="gauge-readiness-badge"
          style={{ backgroundColor: readinessLevel.color }}
        >
          {readinessLevel.label}
        </div>
      )}
    </div>
  );
};

export default ExitReadinessGauge;
